import { useState, useEffect } from "react";

export default function AustinHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Scroll-aware navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className="h-9 w-auto"
              style={{ filter: scrolled ? "none" : "brightness(0) invert(1)" }}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled
                    ? "text-[#1A365D] hover:text-[#F6C344]"
                    : "text-white/90 hover:text-[#F6C344]"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled
                  ? "text-[#1A365D] hover:text-[#F6C344]"
                  : "text-white/90 hover:text-[#F6C344]"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Get Started
            </a>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i
                className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${
                  scrolled ? "text-[#1A365D]" : "text-white"
                }`}
              ></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stunning%20aerial%20panoramic%20view%20of%20Austin%20Texas%20downtown%20skyline%20at%20golden%20hour%20with%20the%20iconic%20Texas%20State%20Capitol%20dome%20prominently%20visible%20on%20Congress%20Avenue%2C%20Colorado%20River%20and%20Lady%20Bird%20Lake%20shimmering%20in%20warm%20sunset%20light%2C%20modern%20glass%20skyscrapers%20alongside%20historic%20limestone%20buildings%2C%20lush%20green%20hills%20in%20the%20background%2C%20professional%20travel%20photography%20with%20rich%20golden%20tones%20and%20dramatic%20sky%2C%20Texas%20pride%20cityscape&width=1800&height=700&seq=austin-hero-2026-v2&orientation=landscape"
          alt="Austin Texas skyline at golden hour with Texas State Capitol and Colorado River"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/45"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left — headline + subtitle + CTA */}
            <div className="flex-1 text-center lg:text-left">
              {/* Location badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-line text-[#F6C344] text-sm"></i>
                <span className="text-white text-xs font-semibold">1005 Congress Ave, Suite 925 &mdash; Downtown Austin, TX</span>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Emergency &amp; Expedited Passport Services in Austin, TX
              </h1>
              <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                Silicon Hills founder boarding for Berlin Tuesday? UT Austin student with a study-abroad program starting in Barcelona in 10 days? Round Rock family flying to Cancun and just realized the kids&rsquo; passports expired? We help Austin residents get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30&ndash;100% lower than FedEx, Staples, and other resellers. As fast as 24 hours. A+ BBB rated.
              </p>
              <a
                href="https://fastpassportcenter.com/checkout/"
                className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
              >
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>

              {/* Trust signals */}
              <div className="mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-8">
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <div className="flex items-center gap-0.5">
                    <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                    <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                    <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                    <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                    <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                  </div>
                  <span className="font-semibold">5.0</span>
                  <span>Reviews</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <i className="ri-award-line text-[#F6C344] text-base"></i>
                  <span>BBB A+</span>
                  <span>Accredited</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <i className="ri-calendar-check-line text-[#F6C344] text-base"></i>
                  <span>20+ Years</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <i className="ri-shield-check-line text-[#F6C344] text-base"></i>
                  <span>Registered State Dept. Courier</span>
                </div>
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <i className="ri-price-tag-3-line text-[#F6C344] text-base"></i>
                  <span>Best Price Guarantee</span>
                </div>
              </div>
            </div>

            {/* Right — Office Photo Card */}
            <div className="w-full lg:w-[360px] flex-shrink-0">
              <div className="bg-[#1A365D]/90 rounded-xl overflow-hidden border border-white/15 shadow-2xl">
                {/* Office photo */}
                <div className="relative overflow-hidden" style={{ height: "220px" }}>
                  <img
                    src="https://storage.readdy-site.link/project_files/5b15eb5b-af79-4c03-9ad0-73c6095d69a7/cb9b1ced-cac4-402c-8493-bf293e553557_compressed_austin-fast-passport-center-office.webp"
                    alt="Fast Passport Center Austin office at 1005 Congress Ave — 10-story downtown building steps from the Texas State Capitol"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A365D]/80 to-transparent px-4 py-3">
                    <p className="text-white text-xs font-semibold">1005 Congress Ave, Suite 925</p>
                    <p className="text-white/60 text-xs">Downtown Austin — steps from the Texas State Capitol</p>
                  </div>
                </div>

                {/* Office info */}
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className="ri-phone-line text-[#F6C344] text-xs"></i>
                    </div>
                    <div>
                      <a href="tel:8772530084" className="text-white text-sm font-semibold hover:text-[#F6C344] transition-colors">
                        (877) 253-0084
                      </a>
                      <p className="text-white/40 text-xs">Nationwide toll-free</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0 mt-0.5">
                      <i className="ri-time-line text-[#F6C344] text-xs"></i>
                    </div>
                    <div className="text-white/70 text-xs space-y-0.5">
                      <p>Mon&ndash;Fri: 9:00 AM &ndash; 6:00 PM CST</p>
                      <p>Sat: 10:00 AM &ndash; 2:00 PM &nbsp;&bull;&nbsp; Sun: Closed</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0 mt-0.5">
                      <i className="ri-walk-line text-[#F6C344] text-xs"></i>
                    </div>
                    <p className="text-white/60 text-xs leading-relaxed">
                      3-minute walk to Texas State Capitol &bull; 9 mi to AUS Airport
                    </p>
                  </div>
                  <div className="pt-2 border-t border-white/10">
                    <a
                      href="https://maps.google.com/maps?daddr=1005+Congress+Ave,+Suite+925,+Austin,+TX+78701"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#F6C344] hover:text-white text-xs font-semibold transition-colors"
                    >
                      <i className="ri-navigation-line"></i> Get Directions on Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}